// Экран кухни. Активация терминала → PIN повара → доска заказов.
// Обновление опросом раз в 4 секунды: на кухонном планшете это надёжнее
// и проще вебсокетов; SSE — оптимизация фазы 2, контракт не изменится.
import { StrictMode, useEffect, useState, useRef } from 'react';
import { registerServiceWorker } from './register-sw';
import { COURSE_NAMES, groupByCourse } from '@dastarhan/core';
import { installCrashReporter } from './crash-report';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActivateResponse, KdsQueue, PosSession,
  type KdsStatus, type KdsTicket,
} from '@dastarhan/contracts';
import { UpdateBanner } from './UpdateBanner';
import './kds.css';

const API = '/api/v1';
const LS = { deviceKey: 'dastarhan.kds.deviceKey', place: 'dastarhan.kds.place', token: 'dastarhan.kds.token' };
const qc = new QueryClient();

const NEXT_LABEL: Record<KdsStatus, string | null> = {
  NEW: 'Взять в работу',
  COOKING: 'Готово',
  READY: 'Выдан',
  SERVED: null,
};
const NEXT_STATUS: Record<KdsStatus, KdsStatus | null> = {
  NEW: 'COOKING', COOKING: 'READY', READY: 'SERVED', SERVED: null,
};
const MODE_RU = { DINE_IN: 'Зал', TAKEAWAY: 'С собой', DELIVERY: 'Доставка' } as const;
const LATE_MIN = 15;

function Gate({ onReady }: { onReady: (token: string) => void }) {
  const [deviceKey, setDeviceKey] = useState(() => localStorage.getItem(LS.deviceKey));
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const activate = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/terminals/activate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      });
      if (!res.ok) { setErr('Код не подошёл'); return; }
      const d = ActivateResponse.parse(await res.json());
      localStorage.setItem(LS.deviceKey, d.deviceKey);
      localStorage.setItem(LS.place, d.locationName);
      setDeviceKey(d.deviceKey);
    } catch { setErr('Нет связи с сервером'); }
    finally { setBusy(false); }
  };

  const login = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/auth/pos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceKey, pin }),
      });
      if (!res.ok) { setErr('PIN не подошёл'); setPin(''); return; }
      const s = PosSession.parse(await res.json());
      localStorage.setItem(LS.token, s.token);
      onReady(s.token);
    } catch { setErr('Нет связи с сервером'); }
    finally { setBusy(false); }
  };

  if (!deviceKey) {
    return (
      <div className="kds-gate">
        <span className="eyebrow">Экран кухни</span>
        <h1>Код активации из бэк-офиса</h1>
        <input placeholder="DSTR-XXXX-XXXX" value={code} autoCapitalize="characters"
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && activate()} />
        <p className="error">{err}</p>
        <button className="btn accent" disabled={busy || code.trim().length < 14} onClick={activate}>
          Активировать
        </button>
      </div>
    );
  }

  return (
    <div className="kds-gate">
      <span className="eyebrow">{localStorage.getItem(LS.place) ?? 'Кухня'}</span>
      <h1>PIN повара</h1>
      <input type="password" inputMode="numeric" placeholder="••••" value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
        onKeyDown={(e) => e.key === 'Enter' && pin.length === 4 && login()} />
      <p className="error">{err}</p>
      <button className="btn accent" disabled={busy || pin.length !== 4} onClick={login}>Войти</button>
    </div>
  );
}

function ageMin(iso: string, now: number): number {
  return Math.floor((now - new Date(iso).getTime()) / 60000);
}

function Ticket({ t, now, onNext, onRevert, station }: { t: KdsTicket; now: number; onNext: () => void; onRevert: () => void; station: string }) {
  const age = ageMin(t.closedAt, now);
  const late = t.status !== 'READY' && age >= LATE_MIN;
  const ageClass = t.status === 'READY' ? '' : age >= LATE_MIN ? 'age-late' : age >= 7 ? 'age-warm' : 'age-fresh';
  const label = NEXT_LABEL[t.status];
  return (
    <article className={`tk ${t.status} ${late ? 'late' : ''} ${ageClass}`}>
      <header>
        <b>№{t.number}</b>
        <span>{t.table ?? MODE_RU[t.mode]}</span>
        <span className={`age ${late ? 'late' : ''}`}>{age} мин</span>
        {t.mode !== 'DINE_IN' && (
          <span className={`mode-badge ${t.mode === 'DELIVERY' ? 'dl' : 'tw'}`}>
            {t.mode === 'DELIVERY' ? '🛵 ДОСТАВКА' : '🥡 С СОБОЙ'}
          </span>
        )}
      </header>
      {/* Курсы: кухня видит волны, а не свалку позиций.
          Заголовок волны появляется только когда курсов больше одного —
          иначе он лишний шум на экране повара */}
      {groupByCourse(t.items).map((g) => (
        <ul key={g.course} className="course-group">
          {groupByCourse(t.items).length > 1 && (
            <li className={`course-head c${g.course}`}>
              {COURSE_NAMES[g.course]}
            </li>
          )}
          {g.items.map((i, idx) => (
            <li key={idx} style={{ opacity: station && (i.station ?? 'kitchen') !== station ? 0.35 : 1 }}>
              <span>
                {i.name}
                {i.mods.length > 0 && <small className="mods"> {i.mods.join(' · ')}</small>}
              </span>
              <b>×{i.qty}</b>
            </li>
          ))}
        </ul>
      ))}
      {label && <button className="go" onClick={onNext}>{label}</button>}
      {t.status === 'READY' && (
        <button className="go revert" title="Вернуть в работу" onClick={onRevert}>↩ В работу</button>
      )}
    </article>
  );
}

function Board({ token, onLogout }: { token: string; onLogout: () => void }) {
  const client = useQueryClient();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, []);

  // Кухня слышит заказ: короткий бип на каждый новый тикет и мигание
  // заголовка вкладки, пока экран не в фокусе. Звук отключаем кнопкой.
  const seen = useRef<Set<string> | null>(null);
  const [sound, setSound] = useState(localStorage.getItem('dastarhan.kdsSound') !== '0');
  const [station, setStation] = useState(localStorage.getItem('dastarhan.kdsStation') ?? '');
  const audio = useRef<AudioContext | null>(null);
  useEffect(() => {
    const unlock = () => {
      audio.current ??= new AudioContext();
      void audio.current.resume();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);
  const beep = () => {
    const ctx = audio.current;
    if (!ctx || ctx.state !== 'running') return;
    [0, 0.22].forEach((dt) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.12, ctx.currentTime + dt);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dt + 0.18);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + dt);
      o.stop(ctx.currentTime + dt + 0.2);
    });
  };
  const blinkTimer = useRef<number | null>(null);
  const startBlink = () => {
    if (blinkTimer.current !== null || !document.hidden) return;
    const base = document.title;
    let on = false;
    blinkTimer.current = window.setInterval(() => {
      on = !on;
      document.title = on ? '● НОВЫЙ ЗАКАЗ' : base;
    }, 900);
    const stop = () => {
      if (blinkTimer.current !== null) window.clearInterval(blinkTimer.current);
      blinkTimer.current = null;
      document.title = base;
      document.removeEventListener('visibilitychange', stop);
    };
    document.addEventListener('visibilitychange', stop);
  };

  const queue = useQuery({
    queryKey: ['kds'],
    queryFn: async () => {
      const res = await fetch(`${API}/kds/queue`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onLogout(); throw new Error('401'); }
      if (!res.ok) throw new Error('HTTP');
      return KdsQueue.parse(await res.json());
    },
    refetchInterval: 4000,
  });

  useEffect(() => {
    const tickets = queue.data?.tickets ?? [];
    if (seen.current === null) {
      seen.current = new Set(tickets.map((t) => t.orderId));
      return;
    }
    const fresh = tickets.filter((t) => t.status === 'NEW' && !seen.current!.has(t.orderId));
    for (const t of tickets) seen.current.add(t.orderId);
    if (fresh.length > 0) {
      if (sound) beep();
      startBlink();
    }
  }, [queue.data, sound]);

  // Кухня имеет право признать «рано»: READY можно вернуть в работу
  const revert = async (t: KdsTicket) => {
    await fetch(`${API}/kds/orders/${t.orderId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: 'COOKING' }),
    }).catch(() => null);
    void client.invalidateQueries({ queryKey: ['kds'] });
  };

  const advance = async (t: KdsTicket) => {
    const next = NEXT_STATUS[t.status];
    if (!next) return;
    await fetch(`${API}/kds/orders/${t.orderId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: next }),
    }).catch(() => null);
    void client.invalidateQueries({ queryKey: ['kds'] });
  };

  const tickets = queue.data?.tickets ?? [];
  // ?board=1 — гостевой экран выдачи на том же терминале
  if (new URLSearchParams(window.location.search).get('board')) {
    return <BoardScreen tickets={tickets} />;
  }
  /*
   * Замок подписки: заказы не идут, и повар должен понимать почему,
   * а не смотреть в пустой экран и звать официанта
   */
  if (queue.data?.lock?.blocked) {
    return (
      <div className="kds-gate">
        <span className="eyebrow">Просрочка {queue.data.lock.days} дн.</span>
        <h1>Экран кухни закрыт</h1>
        <p style={{ color: 'var(--ink-dim)', maxWidth: 420, lineHeight: 1.5 }}>
          {queue.data.lock.message}
        </p>
        <button className="btn small" onClick={() => void queue.refetch()}>
          Проверить снова
        </button>
      </div>
    );
  }

  if (queue.data && !queue.data.kitchen) {
    return (
      <div className="kds-gate">
        <span className="eyebrow">Кухня выключена</span>
        <h1>Этому формату бизнеса кухонный экран не нужен</h1>
        <p style={{ color: 'var(--ink-dim)', maxWidth: 380, lineHeight: 1.5 }}>
          Чеки закрываются сразу как выданные. Если формат сменится на кафе —
          экран оживёт сам, ничего перенастраивать не придётся.
        </p>
        <button className="btn small" title="Звук нового заказа"
          onClick={() => {
            const v = !sound;
            setSound(v);
            localStorage.setItem('dastarhan.kdsSound', v ? '1' : '0');
          }}>{sound ? '🔔' : '🔕'}</button>
        {queue.data?.lock?.kind === 'WARN' && (
          <div className="kds-warn">⚠ {queue.data.lock.message}</div>
        )}
        {(['', 'kitchen', 'bar'] as const).map((st) => (
          <button key={st || 'all'} className={`btn small ${station === st ? 'accent' : ''}`}
            onClick={() => { setStation(st); localStorage.setItem('dastarhan.kdsStation', st); }}>
            {st === '' ? 'Все' : st === 'kitchen' ? '🍳 Кухня' : '🍹 Бар'}
          </button>
        ))}
        <button className="btn" onClick={onLogout}>Выйти</button>
      </div>
    );
  }
  return (
    <div className="kds">
      <header>
        <h1>Кухня · {localStorage.getItem(LS.place) ?? ''}</h1>
        <span className="clock">{new Date(now).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
        <button className="btn" style={{ minHeight: 40 }} onClick={onLogout}>Выйти</button>
      </header>
      <div className="board">
        {queue.isPending && <p className="board-empty">Загружаем очередь…</p>}
        {queue.isError && <p className="board-empty">Нет связи с сервером. Пробуем снова…</p>}
        {queue.isSuccess && tickets.length === 0 && (
          <p className="board-empty">Очередь пуста. Новый заказ появится сам, как только касса его пробьёт.</p>
        )}
        {tickets
          .filter((t) => !station || t.items.some((i) => (i.station ?? 'kitchen') === station))
          .map((t) => (
          <Ticket key={t.orderId} t={t} now={now} station={station}
            onNext={() => advance(t)} onRevert={() => revert(t)} />
        ))}
      </div>
    </div>
  );
}

/** Гостевой экран выдачи: телевизор у кассы. Тот же терминал KDS,
 *  тот же поток данных — другая одежда: огромные номера, ноль кнопок. */
function BoardScreen({ tickets }: { tickets: { orderId: string; number: number; status: string }[] }) {
  const cooking = tickets.filter((t) => t.status === 'NEW' || t.status === 'COOKING');
  const ready = tickets.filter((t) => t.status === 'READY');
  return (
    <div className="board">
      <div className="board-col">
        <h2>Готовим</h2>
        <div className="board-nums">
          {cooking.length === 0 && <span className="board-empty">—</span>}
          {cooking.map((t) => <b key={t.orderId}>{t.number}</b>)}
        </div>
      </div>
      <div className="board-col ready">
        <h2>Заберите</h2>
        <div className="board-nums">
          {ready.length === 0 && <span className="board-empty">—</span>}
          {ready.map((t) => <b key={t.orderId} className="pulse">{t.number}</b>)}
        </div>
      </div>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem(LS.token));
  if (!token) return <Gate onReady={setToken} />;
  return <Board token={token} onLogout={() => { localStorage.removeItem(LS.token); setToken(null); }} />;
}

installCrashReporter('kds');

// Офлайн-оболочка: без неё приложение не откроется без сети
// Область действия берём из адреса: /pos/, /kds/, /menu/ — или корень
registerServiceWorker();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <UpdateBanner />
    <QueryClientProvider client={qc}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
